import React, { useEffect, useState } from "react";
import AdminModal from "components/admin/AdminModal";
import { getUserDetails } from "apis/adminServices/userManagmentService";
import { DetailAirline, User, UserDetails } from "./type";

interface UserDetailsModalProps {
  isOpen: boolean;
  user: User | null;
  onClose: () => void;
}

const EMPTY_VALUE = "—";

// Display convention agreed with the API handoff: "Delta Air Lines (DL)",
// falling back to just the name when the airline has no code.
const airlineLabel = (airline?: DetailAirline | null): string => {
  if (!airline?.airlineName) return "";
  return airline.code
    ? `${airline.airlineName} (${airline.code})`
    : airline.airlineName;
};

const airlineLabels = (airlines?: DetailAirline[] | null): string[] =>
  (airlines ?? []).map(airlineLabel).filter((label) => label !== "");

const DetailField: React.FC<{
  label: string;
  value?: string | number | null;
}> = ({ label, value }) => {
  const text =
    value !== null && value !== undefined ? String(value).trim() : "";
  return (
    <div>
      <p className="text-xs text-lightgray-200 mb-1">{label}</p>
      <p className="text-sm text-grease-100 break-words">
        {text.length > 0 ? text : EMPTY_VALUE}
      </p>
    </div>
  );
};

const AirlineChips: React.FC<{ label: string; airlines: string[] }> = ({
  label,
  airlines,
}) => (
  <div>
    <p className="text-xs text-lightgray-200 mb-2">{label}</p>
    {airlines.length === 0 ? (
      <p className="text-sm text-grease-100">{EMPTY_VALUE}</p>
    ) : (
      <div className="flex flex-wrap gap-2">
        {airlines.map((airline) => (
          <span
            key={airline}
            className="px-3 py-1.5 text-sm text-grease-100 border border-gray-200 rounded-lg"
          >
            {airline}
          </span>
        ))}
      </div>
    )}
  </div>
);

const UserDetailsModal: React.FC<UserDetailsModalProps> = ({
  isOpen,
  user,
  onClose,
}) => {
  const [details, setDetails] = useState<UserDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !user) {
      setDetails(null);
      setError(null);
      return;
    }

    let cancelled = false;

    const fetchDetails = async () => {
      try {
        setLoading(true);
        setError(null);

        // The axios interceptor unwraps to the response body, so `response` is
        // the { isSuccess, message, data } envelope.
        const response = await getUserDetails(user.userId);
        if (cancelled) return;

        if (response?.isSuccess === false) {
          setError(response?.message || "Failed to load user details.");
          return;
        }
        setDetails(response?.data ?? null);
      } catch (err: any) {
        if (cancelled) return;
        console.error("Error fetching user details:", err);
        const apiMessage = err?.response?.data?.message;
        const clientMessage =
          err?.isNetworkError || err?.isTimeout || err?.isRateLimited
            ? err.message
            : null;
        setError(
          apiMessage ||
            clientMessage ||
            "Failed to load user details. Please try again.",
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchDetails();

    // Ignore an in-flight response if the modal closes or the user changes.
    return () => {
      cancelled = true;
    };
  }, [isOpen, user]);

  const additional = details?.additionalAirlines ?? [];
  // The API orders additional airlines by association date and the first three
  // map to the named slots; anything beyond that is still shown so no linked
  // airline is silently dropped.
  const extraAdditional = airlineLabels(additional.slice(3));

  return (
    <AdminModal
      isOpen={isOpen}
      onClose={onClose}
      title="User Details"
      size="2xl"
      showCloseButton={true}
    >
      {loading ? (
        <p className="text-sm text-grease-100 text-center py-6">Loading...</p>
      ) : error ? (
        <p className="text-sm text-red-500 text-center py-6">{error}</p>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 pb-5 border-b border-gray-100">
            <DetailField
              label="Name"
              value={
                details?.fullName?.trim() ||
                (user?.username && user.username.trim().toLowerCase() !== "username"
                  ? user.username.trim()
                  : "")
              }
            />
            <DetailField
              label="Email ID"
              value={details?.email || user?.email}
            />
            <DetailField label="Homebase" value={details?.homebase} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            <DetailField label="Airline" value={details?.airline} />
            <DetailField
              label="Airline ID"
              value={
                details?.airlineId !== null && details?.airlineId !== undefined
                  ? String(details.airlineId)
                  : ""
              }
            />
          </div>

          <div>
            <p className="text-grease-100 text-sm font-semibold mb-4">
              Additional Airlines
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              <DetailField
                label="Primary Additional Airline"
                value={airlineLabel(additional[0])}
              />
              <DetailField
                label="Secondary Additional Airline"
                value={airlineLabel(additional[1])}
              />
              <DetailField
                label="Territory Additional Airline"
                value={airlineLabel(additional[2])}
              />
            </div>
            {extraAdditional.length > 0 && (
              <div className="mt-5">
                <AirlineChips
                  label="Other Additional Airlines"
                  airlines={extraAdditional}
                />
              </div>
            )}
          </div>

          <AirlineChips
            label="Excluded Airlines"
            airlines={airlineLabels(details?.excludedAirlines)}
          />
        </div>
      )}
    </AdminModal>
  );
};

export default UserDetailsModal;
